<script setup lang="ts">
import { useTheme } from '../lib/use-theme.js';
import AppIcon from './AppIcon.vue';
import BrandMark from './BrandMark.vue';

/**
 * The only thing a signed-out visitor sees. One heading, one line, one button:
 * sign-in happens at ATUM Auth and access is granted by the owner, so there is
 * nothing else to read here.
 */
const props = defineProps<{
  state: 'checking' | 'signed-out' | 'unavailable';
  configured: boolean;
  error?: string | undefined;
  reason?: 'expired' | 'signed-out' | undefined;
  busy?: boolean | undefined;
  returnToAtum?: boolean;
}>();
const emit = defineEmits<{ signIn: []; cancelSignIn: []; retry: [] }>();
useTheme();
</script>

<template>
  <main class="signin" aria-labelledby="signin-title">
    <section class="signin-card panel">
      <span class="signin-mark" aria-hidden="true"><BrandMark /></span>
      <div class="signin-heading">
        <h1 id="signin-title">
          {{
            props.returnToAtum && props.state === 'signed-out'
              ? 'Reconnect Remote in ATUM'
              : 'Sign in to ATUM Remote'
          }}
        </h1>
        <p class="signin-lede">Your devices, within reach.</p>
      </div>

      <div v-if="props.state === 'checking'" class="signin-status" role="status">
        <AppIcon name="refresh" class="spin" /><span>Checking your session…</span>
      </div>
      <template v-else-if="props.state === 'unavailable'">
        <div class="banner error" role="alert">
          <AppIcon name="warning" />
          <span>{{ props.error || 'The server could not be reached.' }}</span>
        </div>
        <button type="button" class="primary signin-button" @click="emit('retry')">
          <AppIcon name="refresh" /><span>Try again</span>
        </button>
      </template>
      <template v-else-if="props.returnToAtum">
        <a href="atum://remote" class="primary signin-button">Return to ATUM</a>
        <p class="hint signin-hint">
          In Remote, connect your account again, then reopen the workspace.
        </p>
      </template>
      <template v-else>
        <div v-if="props.error" class="banner error" role="alert">
          <AppIcon name="warning" /><span>{{ props.error }}</span>
        </div>
        <div v-else-if="props.reason === 'expired'" class="banner" role="status">
          <AppIcon name="clock" /><span>Your session ended. Sign in again to continue.</span>
        </div>
        <div v-else-if="props.reason === 'signed-out'" class="banner" role="status">
          <AppIcon name="check" /><span>You are signed out.</span>
        </div>
        <button
          type="button"
          class="primary signin-button"
          :disabled="!props.configured || props.busy"
          @click="emit('signIn')"
        >
          <AppIcon :name="props.busy ? 'refresh' : 'key'" :class="{ spin: props.busy }" />
          <span>{{ props.busy ? 'Waiting for ATUM Auth…' : 'Continue with ATUM Auth' }}</span>
          <AppIcon v-if="!props.busy" name="chevronRight" class="signin-button-arrow" />
        </button>
        <button v-if="props.busy" type="button" class="quiet" @click="emit('cancelSignIn')">
          Cancel sign-in
        </button>
        <p v-if="!props.configured" class="hint signin-hint">
          Owner sign-in is not configured on this server yet.
        </p>
        <p v-else-if="props.busy" class="hint signin-hint">
          Finish in the ATUM Auth window. This tab continues on its own.
        </p>
      </template>

      <p class="signin-fineprint">
        <AppIcon name="lock" /><span
          >Invitation only. The owner grants access; there is no sign-up.</span
        >
      </p>
    </section>
    <footer v-if="!props.returnToAtum" class="signin-foot">
      <a href="https://atum.tech" rel="noreferrer"><BrandMark />Made by ATUM · atum.tech</a>
    </footer>
  </main>
</template>
